import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

const VAPID_PUBLIC = (import.meta.env.VITE_PUSH_VAPID_PUBLIC as string | undefined) ?? '';
const DISMISSED_KEY = 'tapnpay.staff.push.banner.dismissed.v1';

type Permission = NotificationPermission | 'unsupported';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    out[i] = rawData.charCodeAt(i);
  }
  return out;
}

async function saveSubscription(
  sub: PushSubscription,
  storeId: string,
  employeeId: string,
): Promise<void> {
  const raw = sub.toJSON();
  try {
    await supabase.functions.invoke('tapnpay-push-subscribe', {
      body: {
        endpoint: raw.endpoint,
        keys: raw.keys,
        role: 'staff',
        store_id: storeId,
        employee_id: employeeId,
      },
    });
  } catch {
    // Best-effort fallback: write to public.push_subscriptions if it exists
    try {
      await supabase.from('push_subscriptions').upsert(
        {
          endpoint: raw.endpoint,
          keys: raw.keys,
          role: 'staff',
          store_id: storeId,
          employee_id: employeeId,
        },
        { onConflict: 'endpoint' },
      );
    } catch {
      // swallow — push subscription is best-effort
    }
  }
}

export interface UsePushSubscriptionOptions {
  storeId: string | null;
  employeeId: string | null;
}

export function usePushSubscription({ storeId, employeeId }: UsePushSubscriptionOptions) {
  const [permission, setPermission] = useState<Permission>(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  );
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [error, setError] = useState<string | null>(null);

  const enable = useCallback(async () => {
    if (!storeId || !employeeId) return;
    if (typeof Notification === 'undefined') {
      setPermission('unsupported');
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return;

      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setError('push_not_supported');
        return;
      }

      const reg =
        (await navigator.serviceWorker.getRegistration()) ??
        (await navigator.serviceWorker.register('/sw.js'));

      if (!VAPID_PUBLIC) {
        setError('vapid_missing');
        return;
      }

      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
        }));
      await saveSubscription(sub, storeId, employeeId);
    } catch (e) {
      setError((e as Error)?.message ?? 'push_failed');
    }
  }, [storeId, employeeId]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  }, []);

  // Re-sync subscription on mount if user has already granted permission
  useEffect(() => {
    if (!storeId || !employeeId) return;
    if (permission !== 'granted') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (!sub || cancelled) return;
        await saveSubscription(sub, storeId, employeeId);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId, employeeId, permission]);

  const showBanner = permission === 'default' && !dismissed;

  return { permission, enable, dismiss, showBanner, error };
}
